/**
 * Barcode utility functions for handling format differences
 * (EAN-13 vs UPC-A, leading zeros, whitespace, etc.)
 */

/**
 * Normalize barcode by removing leading zeros (handles EAN-13 to UPC-A conversion)
 * @param barcode - The barcode to normalize
 * @returns Normalized barcode string
 */
export function normalizeBarcode(barcode: string): string {
  if (!barcode) return "";
  // Remove leading zeros but keep at least one character
  return barcode.replace(/^0+/, "") || "0";
}

/**
 * Check if two barcodes match, accounting for format differences
 * Handles EAN-13 vs UPC-A (leading zeros), whitespace, etc.
 * @param barcode1 - First barcode
 * @param barcode2 - Second barcode
 * @returns True if barcodes represent the same product
 */
export function barcodesMatch(
  barcode1: string | undefined,
  barcode2: string | undefined,
): boolean {
  if (!barcode1 || !barcode2) return false;

  // Trim whitespace
  const b1 = barcode1.trim();
  const b2 = barcode2.trim();

  // Direct match
  if (b1 === b2) return true;

  // Normalize (remove leading zeros) and compare
  const normalized1 = normalizeBarcode(b1);
  const normalized2 = normalizeBarcode(b2);
  if (normalized1 === normalized2) return true;

  // Check if one ends with the other (handles EAN-13 with leading zero vs UPC-A)
  if (b1.endsWith(b2) || b2.endsWith(b1)) return true;

  return false;
}
